import React, { useEffect, useMemo } from 'react'
import { BusinessOutlined, CancelOutlined, CheckCircleOutlineOutlined, LocalHospitalOutlined, NoteAddOutlined, PaymentsOutlined } from '@mui/icons-material';
import { Box, FormControl, Grid, IconButton, Typography, useTheme } from '@mui/material';
import { PracticeInfo } from '../../../redux/slice/practiceSlice';
import { useAppDispatch } from '../../../redux/slice/redux-hooks';
import Button from '../../common/Button';
import PageBackButton from '../../common/PageBackButton';
import { useLocation, useNavigate } from 'react-router-dom';
import CustomRadioButton from '../../common/CustomRadioButton';
import { useForm } from 'react-hook-form';
import CustomTextField from '../../common/CustomTextField';
import Table from '../../common/CustomTable';
import CustomDropdownField from '../../common/CustomDropdownFiled';
import {
  AgencyExecutives, PracticeExecutive, PracticeLocationInfo, PracticeProviderInfo, RejectPractice, approvePracticeRequest, fetchActiveAgencyExecutives,
  fetchActiveBillers, rejectPractice, usePracticeApproveSlice
} from '../../../redux/slice/practice/practiceApproveSlice';
import PracticeApprovePopup from './PracticeApprovePopup';
import { formatAddress, fullName, getCategoryDisplayLabel } from '../../../utils/Utility';
import { tokens } from '../../../theme';
import CustomEmailChipField from '../../common/CustomEmailChipField';
import OptionsPopup, { OptionPopupObjectProps } from '../../common/OptionsPopup';
import { SingleAlertInfo, fetchAgenciesList, setSingleAlertObj, useCommonReducer } from '../../../redux/slice/commonSlice';
import CustomMultipleDropDown from '../../common/CustomMultipleDropDown';
import { useNavigation } from '../../../hooks/useNavigation';
import { useCodetablesSlice } from '../../../redux/slice/codetableSlice';
import { PRODUCT_CATEGORY } from '../../../constants/applicationConstants';
import { SectionHeader } from '../../ui/ContentSection';
import { useModalStyles } from '../../common/useModalStyles';


interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: "left" | "center" | "right";
  format?: (value: number) => string;
}

type Option = {
  value: string | number;
  label: string;
};

interface ApprovePracticeUIState {
  practiceId: string | undefined,
  practiceExecutive: PracticeExecutive[] | null,
  agencyId: number | null;
  // notificationEmails: string,
  approvalComments: string,
  billerId: number | null,
  billingType: string | null,
  practiceName: string | null,
  discount: string[] | null,
  practiceExDropDown: any,
  billCommercial: boolean | null
  billMedicare: boolean | null
  billMedicareAdvantage: boolean | null
  billingCollagen: boolean | null
  dmeLicense: boolean | null
};

const locationColumns: Column[] = [
  { id: "id", label: "Practice Location Address", minWidth: 100 },
];

const providerColumns: Column[] = [
  { id: "name", label: "Provider Name", minWidth: 100 },
  { id: "npi", label: "Provider NPI", minWidth: 100 },
  { id: "ptan", label: "Provider PTAN", minWidth: 100 },
  { id: "email", label: "Provider Email", minWidth: 100 },
];

function PracticeRequest() {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { navigate } = useNavigation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const {
    tokens: mTokens,
    isDark: isDarkModal,
    primaryBtnSx,
    cancelBtnSx,
    inputFieldSx,
    dropdownWrapSx,
  } = useModalStyles();
  const cardSx = {
    borderRadius: "16px",
    border: `1px solid ${mTokens.border}`,
    backgroundColor: mTokens.bg,
    backgroundImage: "none",
    overflow: "hidden" as const,
    boxShadow: isDarkModal ? "0 10px 20px rgba(0,0,0,0.1)" : "none",
  };
  const FieldLabel = ({ label, required = false }: { label: string; required?: boolean }) => (
    <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: mTokens.textSecondary, mb: 0.7, display: "flex", alignItems: "center", gap: 0.4, flexWrap: "wrap" }}>
      {label}{required && <Box component="span" sx={{ color: mTokens.accent }}>*</Box>}
    </Typography>
  );
  const outlineActionBtnSx = {
    ...cancelBtnSx,
    color: mTokens.accent,
    borderColor: `${mTokens.accent}8c`,
    backgroundColor: isDarkModal ? "rgba(20,184,166,0.08)" : "rgba(20,184,166,0.04)",
    height: "42px",
    "&:hover": {
      color: "#fff",
      borderColor: mTokens.accent,
      backgroundColor: mTokens.accent,
      boxShadow: "0 6px 22px rgba(20,184,166,0.28)",
      "& .MuiSvgIcon-root": { color: "#fff" },
    },
    "& .MuiTypography-root": { color: "inherit", fontWeight: 700 },
    "& .MuiButton-startIcon, & .MuiSvgIcon-root": { color: "inherit" },
  };
  const tablePanelSx = {
    border: `1px solid ${mTokens.border}`,
    borderRadius: "12px",
    overflow: "hidden" as const,
    backgroundColor: isDarkModal ? "rgba(15,23,42,0.35)" : "rgba(248,250,252,0.85)",
    p: 1,
  };
  const controlHeightSx = {
    minHeight: "42px !important",
    height: "42px !important",
    maxHeight: "42px !important",
    boxSizing: "border-box !important",
    alignItems: "center",
  };
  const multiDropdownWrapSx = (hasError = false) => {
    const base = dropdownWrapSx(hasError);
    const nested = (base as Record<string, unknown>)["& .MuiOutlinedInput-root, & .MuiInputBase-root"];
    const chipBase = (base as Record<string, unknown>)["& .MuiChip-root"];
    return {
      ...base,
      width: "100%",
      maxWidth: "100%",
      minWidth: 0,
      overflow: "hidden",
      "& .MuiAutocomplete-root, & .MuiFormControl-root, & .MuiTextField-root": {
        width: "100%",
        maxWidth: "100%",
      },
      "& .MuiOutlinedInput-root, & .MuiInputBase-root": {
        ...(nested && typeof nested === "object" ? nested : {}),
        ...controlHeightSx,
        flexWrap: "nowrap !important",
        overflow: "hidden !important",
        width: "100%",
        maxWidth: "100%",
        padding: "0 38px 0 12px !important",
      },
      "& .MuiAutocomplete-root .MuiInputBase-input, & .MuiAutocomplete-input": {
        minWidth: "0 !important",
        width: "0 !important",
        flexGrow: 1,
        padding: "0 !important",
      },
      "& .MuiChip-root": {
        ...(chipBase && typeof chipBase === "object" ? chipBase : {}),
        maxWidth: "110px",
        height: "24px !important",
        flexShrink: 1,
        minWidth: 0,
      },
      "& .MuiChip-label": {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
      "& .MuiFormHelperText-root": {
        marginLeft: 0,
        marginRight: 0,
      },
    };
  };
  const tableRadioSx = {
    color: mTokens.textSecondary,
    "&.Mui-checked": { color: `${mTokens.accent} !important` },
  };
  const {  agencyExecutives, billerList, errorList } = usePracticeApproveSlice();
  const { agencyInfoList } = useCommonReducer();
  const { allCodeTablesValuesMap } = useCodetablesSlice();
  const [locationRows, setLocationRows] = React.useState<{ id: string }[]>([]);
  const [providerRows, setProviderRows] = React.useState<{ name: string, npi: string, ptan: string, email: string }[]>([]);
  let [practice, setPractice] = React.useState<PracticeInfo>();
  const { control, handleSubmit, setValue, getValues, reset, watch, formState: { errors } } = useForm<ApprovePracticeUIState>();
  const [showPracticesPopup, setShowPracticesPopup] = React.useState(false);
  const [isRejectValidations, setIsRejectValidations] = React.useState(false);
  const [showOptionPopup, setShowOptionsPopup] = React.useState<OptionPopupObjectProps>({message:'', show:false});
  const [showDmeLicense, setShowDmeLicense] = React.useState<boolean | null>(null);
  const [showBillingCollagen, setShowBillingCollagen] = React.useState<boolean | null>(null);
  const [showBillMedicare, setShowBillMedicare] = React.useState<boolean | null>(null);
  const [showBillMedicareAdvantage, setShowBillMedicareAdvantage] = React.useState<boolean | null>(null);
  const [showBillCommercial, setShowBillCommercial] = React.useState<boolean | null>(null);
  let initialValues: ApprovePracticeUIState = {
    practiceId: '',
    practiceExecutive: null,
    agencyId: null,
    // notificationEmails: '',
    approvalComments: '',
    billerId: null,
    billingType: null,
    practiceName: null,
    discount: null,
    practiceExDropDown: null,
    billCommercial: null,
    billMedicare: null,
    billMedicareAdvantage: null,
    billingCollagen: null,
    dmeLicense: null
  };

  useEffect(() => {
    const practiceRow = location.state.rowValue;
    setDefaultValues();
    if (practiceRow) {
      setPractice(practiceRow);
      setValue("practiceId", practiceRow.practiceId);
      setValue("billingType", practiceRow.billingType);
      setValue("practiceName", practiceRow.practiceName);
      const practiceInfo = practiceRow?.practiceRequestInfo ?? {};

      setValue("dmeLicense", practiceInfo?.dmeLicense != null ? practiceInfo.dmeLicense.toString() : null);
      setValue("billingCollagen", practiceInfo?.billingCollagen != null ? practiceInfo.billingCollagen.toString() : null);
      setValue("billMedicare", practiceInfo?.billMedicare != null ? practiceInfo.billMedicare.toString() : null);
      setValue("billMedicareAdvantage", practiceInfo?.billMedicareAdvantage != null ? practiceInfo.billMedicareAdvantage.toString() : null);
      setValue("billCommercial", practiceInfo?.billCommercial != null ? practiceInfo.billCommercial.toString() : null);
      
      setShowDmeLicense(practiceInfo?.dmeLicense ?? null);
      setShowBillingCollagen(practiceInfo?.billingCollagen ?? null);
      setShowBillMedicare(practiceInfo?.billMedicare ?? null);
      setShowBillMedicareAdvantage(practiceInfo?.billMedicareAdvantage ?? null);
      setShowBillCommercial(practiceInfo?.billCommercial ?? null);
    }
    dispatch(fetchActiveAgencyExecutives({getAgencyAdmins: true, isInactive: false}));
    dispatch(fetchActiveBillers());
    dispatch(fetchAgenciesList());
  
    setLocations(practiceRow);
    setProviders(practiceRow);
  }, []);

  const setDefaultValues = () => {
    reset(initialValues);
  };

  const setLocations = (practiceRow: any) => {
    const locationsArr: { id: string; }[] = [];
    practiceRow && practiceRow.practiceLocations?.forEach((location: PracticeLocationInfo) => {
      locationsArr.push({
        id: formatAddress(location.streetAddress, location.unitNumber, location.city, location.state, location.zipCode)
      });
    });
    setLocationRows(locationsArr);
  };

  const setProviders = (practiceRow: any) => {
    const providersArr: { name: string, npi: string, ptan: string, email: string }[] = [];
    practiceRow && practiceRow.practiceProviders?.forEach((provider: PracticeProviderInfo) => {
      providersArr.push({
        name: provider.title + " " + provider.firstName + " " + provider.middleName + " " + provider.lastName,
        npi: provider.npi,
        ptan: provider.ptan,
        email: provider.email || ''
      });
    });
    setProviderRows(providersArr);
  }

  const onSubmit = async () => {
    setAgencyDropdowns();
    const {  practiceName, discount, practiceExDropDown, ...approvePracticeObj } = getValues();
    approvePracticeObj.billingType = getValues("billingType");
    approvePracticeObj.dmeLicense = getValues("dmeLicense");
    approvePracticeObj.billingCollagen = getValues("billingCollagen");
    approvePracticeObj.billMedicare = getValues("billMedicare");
    approvePracticeObj.billMedicareAdvantage = getValues("billMedicareAdvantage");
    approvePracticeObj.billCommercial = getValues("billCommercial");
    
    setShowOptionsPopup({
      message: "Do you want to Approve the practice? ",
      show: true,
      variant: "confirm",
      buttons: [{ name: "Confirm", color: 'primary', onClick: () => approvePractice(approvePracticeObj) }, { name: "Cancel", color: 'secondary', onClick: cancelOptionPopup }]
    });
  };

  const approvePractice = async (approvePracticeObj: any) => {
      const response = await dispatch(approvePracticeRequest(approvePracticeObj));
      if(response.meta.requestStatus === "fulfilled") {
        let successObj: SingleAlertInfo = {
          message: `Practice ${practice && practice.practiceName} has been Approved.`,
          alertType: "success"
        };
        dispatch(setSingleAlertObj(successObj));
        navigate("/practice_list");
      };
    cancelOptionPopup(); 
  };

  const cancelOptionPopup = () => {
    setShowOptionsPopup({ message: "", show: false });
  };

  const clickShowPracticesPopup = () => {
    setShowPracticesPopup(true);
  };

  const clickClosePracticesPopup = () => {
    setShowPracticesPopup(false);
  };

  const reject = async () => {
    const formData = getValues();
    let message: string = "";
    let type: "error" | "success" | "warning" | "info" = "info";
      try {
        let rejectDataObj : RejectPractice = {
          practiceId : practice && practice.practiceId,
          approvalComments : formData.approvalComments
        };
        await dispatch(
          rejectPractice(rejectDataObj)
        ).then((res) => {
          if(res.payload?.message === "SUCCESS"){
            message = `Practice ${practice && practice.practiceName} has been Rejected.`;
            type = "warning";
          }else{
            //@ts-ignore
            message = res.payload ? res.payload.message : res.console.error.message;
            type = "error"
          }
          let successObj: SingleAlertInfo = {
            message: message,
            alertType: type
          }
          dispatch(setSingleAlertObj(successObj));
          // navigate("/practice_list", {state: {status: message, alertType: type}});
          navigate(-1);
        });
      } catch (e) {
        console.error(e);
    };
    cancelOptionPopup(); 
  };

  const handleReject = async () => {

    // const formData = getValues();
    // let errorObjs = [];
    // if(formData.approvalComments === null || formData.approvalComments === ""){
    //   let errorObj = {};
    //   errorObj = {
    //     errIdentifier : "approvalComments",
    //     errMessage : "Approval Comments are required."
    //   };
    //   errorObjs.push(errorObj)
    //   dispatch(updatePracticeApproveErrorList(errorObjs));
    //   let newFormData = {...formData, notificationEmails: "", practiceExDropDown: null}
    //   reset(newFormData);

    //   let errObj: SingleAlertInfo = {
    //     message: "Additional notes / comments required.",
    //     alertType: "error"
    //   };
    //   dispatch(setSingleAlertObj(errObj));
    // } else {
      setShowOptionsPopup({
        message: "Do you want to Reject the practice? ",
        show: true,
        variant: "confirm",
        buttons: [{ name: "Confirm", color: 'primary', onClick: reject }, { name: "Cancel", color: 'secondary', onClick: cancelOptionPopup }]
      });
    // };
  };

  const practiceExOps = useMemo(() => {
    let options: Option[] = [];
    if(getValues('agencyId') && agencyExecutives) {
      agencyExecutives?.map(executive => {
        if(executive.agencyId === getValues('agencyId') )
        options.push({ value: executive.userId, label: executive.agencyName + " - " + fullName(null,executive.firstName, executive.middleName, executive.lastName) });
      });
    }
    return options;
  }, [watch('agencyId')]);

  const billerOptions = useMemo(() => {
    let options: Option[] = [];
    billerList?.map((option) => {
      options.push({ value: option.billerId, label: option.billerName });
    });
    return options;
  }, [billerList]);

  const agencyList = useMemo(() => {
    let options: Option[] = [];
    agencyInfoList?.map((option) => {
      options.push({ label: option.agencyName, value: option.agencyId });
    });
    return options;
  }, [agencyInfoList]);

  const approveValidationRules = {
    practiceAgency : { required: "Agency is required." },
    notificationEmails : {required: "Notification Emails are required." },
  };

  useEffect(() => {
    if(isRejectValidations){
      handleReject();
      setIsRejectValidations(false);
    }
  }, [isRejectValidations]);

  //CS-955
  const handleFieldChange = (field: keyof ApprovePracticeUIState, value: string | null) => {
    setValue(field, value);
    if (field === "dmeLicense") {
      setShowDmeLicense(value === "true");
      if (value !== "true") {
        setValue("billingCollagen", null);
        setValue("billMedicare", null);
        setValue("billMedicareAdvantage", null);
        setValue("billCommercial", null);
        setShowBillingCollagen(null);
        setShowBillMedicare(null);
        setShowBillMedicareAdvantage(null);
        setShowBillCommercial(null);
      }
    } else if (field === "billingCollagen") {
      setShowBillingCollagen(value === "true");
      if (value !== "true") {
        setValue("billMedicare", null);
        setValue("billMedicareAdvantage", null);
        setValue("billCommercial", null);
        setShowBillMedicare(null);
        setShowBillMedicareAdvantage(null);
        setShowBillCommercial(null);
      }
    }
  };

  const validationRules = {
    dmeLicense: { required: "Please select an option for DME license." },
    billingCollagen: { required: `Please select an option for billing ${getCategoryDisplayLabel(allCodeTablesValuesMap, PRODUCT_CATEGORY.CONSERVATIVE)}.` },
  };

  let errorIdentifiers = [];
  let errorMessages = [];

  if(errorList){
    for(let i=0; i< errorList.length; i++) {
      //@ts-ignore
      errorIdentifiers.push(errorList[i].errIdentifier);
  }
}

const setAgencyDropdowns = () => {
  const exIds = getValues('practiceExDropDown');
  const practiceExs: PracticeExecutive[] = [] 
  exIds?.forEach((id: number) => {
    let selectedExecutive: AgencyExecutives | undefined = agencyExecutives?.find(ex => ex.userId === id);
    if(selectedExecutive) {
      practiceExs.push({ userId: selectedExecutive.userId, agencyId: selectedExecutive.agencyId });
    }
  });

  setValue("practiceExecutive", practiceExs);
}

  return (
    <>
      {showOptionPopup.show &&
        <OptionsPopup
          open={showOptionPopup.show}
          onClose={() => setShowOptionsPopup({ message: "", show: false })}
          variant={showOptionPopup.variant}
          message={<>{showOptionPopup.message}</>}
          buttons={showOptionPopup.buttons}
          title={showOptionPopup.title}
          obj={showOptionPopup.obj}
        />
      }
    <PracticeApprovePopup onClose={clickClosePracticesPopup} open={showPracticesPopup} />
    <Box sx={{ mx: { xs: "10px", sm: "20px" }, mt: "12px", pb: "72px" }}>
      <Box mb={2} display="flex" alignItems="flex-start" gap={1.5}>
        <PageBackButton tooltip={null} />
        <Box data-testid="mock-header">
          <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: mTokens.textSecondary, textTransform: "uppercase", letterSpacing: "0.12em", mb: 0.3 }}>
            Practices / Approval
          </Typography>
          <Typography component="h1" sx={{ fontSize: "1.6rem", fontWeight: 800, color: mTokens.textPrimary, lineHeight: 1.1 }}>
            Practice Approval
          </Typography>
          <Typography sx={{ fontSize: "0.78rem", color: mTokens.textSecondary, mt: 0.4 }}>
            Review and approve or reject a submitted practice request.
          </Typography>
        </Box>
      </Box>

      <form>
        <Box data-testid="mock-card" sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={cardSx}>
            <SectionHeader icon={<BusinessOutlined fontSize="small" />} title="Practice Summary" />
            <Box sx={{ px: { xs: 2, md: 2.8 }, py: 2 }}>
              <Grid container spacing={2} alignItems="flex-end">
                <Grid item md={6} xs={12}>
                  <FieldLabel label="Practice Name" />
                  <Box sx={inputFieldSx(!!errors.practiceName)}>
                    <CustomTextField control={control} label="" controllerName="practiceName" size="small" />
                  </Box>
                </Grid>
                <Grid item md={6} xs={12}>
                  <Button
                    fullWidth
                    size="large"
                    startIcon={<NoteAddOutlined />}
                    variant="outlined"
                    removeUpperCase
                    onClick={clickShowPracticesPopup}
                    sx={{ ...outlineActionBtnSx, maxWidth: { sm: 220 }, ml: { sm: "auto" }, display: "flex" }}
                  >
                    Practice Request
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Box>

          <Box sx={cardSx}>
            <SectionHeader icon={<LocalHospitalOutlined fontSize="small" />} title="Providers & Locations" />
            <Box sx={{ px: { xs: 1.5, md: 2.8 }, py: 2 }}>
              <Grid container spacing={2}>
                <Grid item md={6} xs={12}>
                  <Box sx={{ ...tablePanelSx, overflowX: "auto" }}>
                    <Table
                      columns={providerColumns}
                      rows={providerRows}
                      disablePagination={true}/>
                  </Box>
                </Grid>
                <Grid item md={6} xs={12}>
                  <Box sx={{ ...tablePanelSx, overflowX: "auto" }}>
                    <Table
                      columns={locationColumns}
                      rows={locationRows}
                      disablePagination={true} />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>

          <Box sx={cardSx}>
            <SectionHeader icon={<PaymentsOutlined fontSize="small" />} title="Billing & Assignment" />
            <Box sx={{ px: { xs: 2, md: 2.8 }, py: 2 }}>
              <Grid container spacing={2}>
                <Grid item md={12} xs={12}>
                  <FieldLabel label="How the practice bills?" />
                  <FormControl>
                    <CustomRadioButton
                      control={control}
                      controllerName="billingType"
                      options={[{ value: "INDIVIDUAL", label: "Individual NPI" }, { value: "GROUP", label: "Group NPI" }]}
                      radioSx={tableRadioSx}
                    />
                  </FormControl>
                </Grid>
                {/* CS-955 */}
                {showDmeLicense !== null && (
                    <Grid item md={12} xs={12}>
                      <Grid container spacing={2}>
                        <Grid
                          item
                          xs={12}
                          sx={{
                            display: {
                              xs: "block",
                              md: "flex",
                            },
                            alignItems: {
                              md: "center",
                            },
                            gap: { md: 2 },
                          }}
                        >
                          <FieldLabel label="Do you have a DME license?" required />
                          <CustomRadioButton
                            control={control}
                            controllerName="dmeLicense"
                            options={[
                              { value: true, label: "Yes" },
                              { value: false, label: "No" },
                            ]}
                            rules={{ required: "Please select an option for DME license." }}
                            onChange={(e) =>
                              handleFieldChange("dmeLicense", e.target.value)
                            }
                            radioSx={tableRadioSx}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  )}
                  {showDmeLicense && (
                    <>
                      <Grid item md={12} xs={12}>
                        <Grid container spacing={2}>
                          <Grid
                            item
                            xs={12}
                            sx={{
                              display: {
                                xs: "block",
                                md: "flex",
                              },
                              alignItems: {
                                md: "center",
                              },
                              gap: { md: 2 },
                            }}
                          >
                            <FieldLabel label={`Will you be billing ${getCategoryDisplayLabel(allCodeTablesValuesMap, PRODUCT_CATEGORY.CONSERVATIVE)}?`} required />
                            <CustomRadioButton
                              control={control}
                              controllerName="billingCollagen"
                              options={[
                                { value: true, label: "Yes" },
                                { value: false, label: "No" },
                              ]}
                                rules={{ required: `Please select an option for billing ${getCategoryDisplayLabel(allCodeTablesValuesMap, PRODUCT_CATEGORY.CONSERVATIVE)}.` }}
                              onChange={(e) =>
                                handleFieldChange(
                                  "billingCollagen",
                                  e.target.value
                                )
                              }
                              radioSx={tableRadioSx}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                      {showBillingCollagen && (
                        <>
                          <Grid item md={12} xs={12}>
                            <Grid container spacing={2}>
                              <Grid
                                item
                                xs={12}
                                sx={{
                                  display: {
                                    xs: "block",
                                    md: "flex",
                                  },
                                  alignItems: {
                                    md: "center",
                                  },
                                  gap: { md: 2 },
                                }}
                              >
                                <FieldLabel label="Will you bill Medicare?" required />
                                <CustomRadioButton
                                  control={control}
                                  controllerName="billMedicare"
                                  options={[
                                    { value: true, label: "Yes" },
                                    { value: false, label: "No" },
                                  ]}
                                  rules={{ required: "Please select an option for billing Medicare." }}
                                  radioSx={tableRadioSx}
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid item md={12} xs={12}>
                            <Grid container spacing={2}>
                              <Grid
                                item
                                xs={12}
                                sx={{
                                  display: {
                                    xs: "block",
                                    md: "flex",
                                  },
                                  alignItems: {
                                    md: "center",
                                  },
                                  gap: { md: 2 },
                                }}
                              >
                                <FieldLabel label="Will you bill Medicare Advantage?" required />
                                <CustomRadioButton
                                  control={control}
                                  controllerName="billMedicareAdvantage"
                                  options={[
                                    { value: true, label: "Yes" },
                                    { value: false, label: "No" },
                                  ]}
                                  rules={{ required: "Please select an option for billing Medicare Advantage." }}
                                  radioSx={tableRadioSx}
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid item md={12} xs={12}>
                            <Grid container spacing={2}>
                              <Grid
                                item
                                xs={12}
                                sx={{
                                  display: {
                                    xs: "block",
                                    md: "flex",
                                  },
                                  alignItems: {
                                    md: "center",
                                  },
                                  gap: { md: 2 },
                                }}
                              >
                                <FieldLabel label="Will you bill Commercial?" required />
                                <CustomRadioButton
                                  control={control}
                                  controllerName="billCommercial"
                                  options={[
                                    { value: true, label: "Yes" },
                                    { value: false, label: "No" },
                                  ]}
                                  rules={{ required: "Please select an option for billing Commercial." }}
                                  radioSx={tableRadioSx}
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                        </>
                      )}
                    </>
                  )}
                  {/*  */}
                <Grid item xs={12} md={4}>
                {/* CS-1334 - Start */}
                  <FieldLabel label="Practice Biller" />
                  <Box sx={dropdownWrapSx(!!errors.billerId)}>
                    <CustomDropdownField control={control} name='billerId' options={billerOptions} label="" size="small"/>
                  </Box>
                </Grid>
                <Grid item xs={12} md={8} sx={{ display: { xs: "none", md: "block" } }} />
                <Grid item xs={12} md={4}>
                  <FieldLabel label="Agency" />
                  <Box sx={dropdownWrapSx(!!errors.agencyId)}>
                    <CustomDropdownField control={control} name='agencyId' options={agencyList} label="" size="small" rules={isRejectValidations ? {} : approveValidationRules.practiceAgency}/>
                  </Box>
                {/* CS-1334 - End */}
                </Grid>
                <Grid item xs={12} md={8} sx={{ display: { xs: "none", md: "block" } }} />
                <Grid item xs={12} md={4} sx={{ minWidth: 0 }}>
                  <FieldLabel label="Account Representative" />
                  <Box sx={multiDropdownWrapSx(!!errors.practiceExDropDown)}>
                    <CustomMultipleDropDown control={control} name='practiceExDropDown' options={practiceExOps} label="" size="small" />
                  </Box>
                </Grid>
                <Grid item xs={12} md={8} sx={{ display: { xs: "none", md: "block" } }} />

                {/* <Grid item md={6} xs={12}>
                  <CustomEmailChipField control={control} label="Emails that should receive submission notifications:" controllerName="notificationEmails" rules={isRejectValidations ? {} : approveValidationRules.notificationEmails} />
                </Grid> 
                <Grid item md={4}></Grid>*/}
                <Grid item md={6} xs={12}>
                  <FieldLabel label="Additional notes/comments" />
                  <Box sx={inputFieldSx(!!errors.approvalComments)}>
                    <CustomTextField control={control} label="" controllerName="approvalComments" multiline rows={3} size="small"
                   />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>

          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            gap={1.2}
            justifyContent="flex-end"
          >
            <Button
              fullWidth
              type='submit'
              startIcon={<CheckCircleOutlineOutlined />}
              onClick={handleSubmit(onSubmit)}
              removeUpperCase
              sx={{
                ...primaryBtnSx,
                minWidth: { sm: 140 },
                width: { xs: "100%", sm: "auto" },
                backgroundColor: mTokens.accent,
                boxShadow: "0 4px 18px rgba(20,184,166,.35)",
                color: "#fff",
                "& .MuiTypography-root": { color: "#fff", fontWeight: 700 },
                "& .MuiButton-startIcon, & .MuiSvgIcon-root": { color: "#fff" },
              }}
            >
              Approve
            </Button>
            <Button
              fullWidth
              color={"error"}
              variant="outlined"
              type="submit"
              onClick={(e) => {setIsRejectValidations(true); e.preventDefault();}}
              startIcon={<CancelOutlined />}
              removeUpperCase
              sx={{
                ...cancelBtnSx,
                minWidth: { sm: 140 },
                width: { xs: "100%", sm: "auto" },
                "& .MuiTypography-root": { color: "inherit", fontWeight: 700 },
                "& .MuiButton-startIcon, & .MuiSvgIcon-root": { color: "inherit" },
              }}
            >
              Reject
            </Button>
          </Box>
        </Box>
      </form>
      </Box>
    </>

  );
}

export default PracticeRequest